import type { ReactNode } from 'react';
import { ImageIcon, RefreshCw } from 'lucide-react';
import { Button, Card, EmptyState } from '@/components/ui';
import { useRequestPreview } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import { siteUrl } from '@/utils/siteUrl';
import type { SiteDetail } from '@/types/api';

/**
 * The site's front page as a visitor sees it (ADR-0034): a picture taken
 * daily and on request. A failed try leaves the last good picture, and says
 * why the new one is not there.
 */
export function PreviewCard({ site, mayRefresh }: { readonly site: SiteDetail; readonly mayRefresh: boolean }): ReactNode {
  const request = useRequestPreview(site.id, {
    onSuccess: () => toast.success('Снимок запрошен'),
    onError: (error) => toast.error('Не удалось запросить снимок', errorText(error)),
  });
  const { preview } = site;
  // The picture's time in the address: a new picture is a new address, the same one is kept.
  const src = preview.takenAt ? `/api/v1/sites/${site.id}/preview?t=${encodeURIComponent(preview.takenAt)}` : null;

  return (
    <Card
      title="Снимок главной"
      icon={ImageIcon}
      actions={
        mayRefresh ? (
          <Button size="sm" variant="ghost" icon={RefreshCw} busy={request.isPending} disabled={preview.requested} onClick={() => request.mutate()}>
            {preview.requested ? 'Снимок в очереди' : 'Обновить'}
          </Button>
        ) : null
      }
    >
      <div className="stack stack-tight">
        {src ? (
          <a href={site.primaryDomain ? siteUrl(site.primaryDomain) : undefined} target="_blank" rel="noreferrer noopener">
            <img
              src={src}
              alt={`Главная страница ${site.primaryDomain ?? site.displayName}`}
              loading="lazy"
              width={1366}
              height={768}
              style={{ width: '100%', maxWidth: 720, height: 'auto', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}
            />
          </a>
        ) : (
          <EmptyState icon={ImageIcon} message={preview.error ? 'Снимок не получен' : 'Снимка ещё нет'} />
        )}
        <span className="text-sm muted">
          {preview.takenAt ? `Снимок ${formatRelative(preview.takenAt)}` : ''}
          {preview.error ? `${preview.takenAt ? ' · ' : ''}ошибка последней попытки: ${preview.error}` : ''}
        </span>
      </div>
    </Card>
  );
}
